import * as prismic from '@prismicio/client'
import * as Sentry from '@sentry/nextjs'
import KvikaClient from '@kvika/api-client'
import AudurClient from '@kvika/audur-api-client-v2'
import Cookies from 'js-cookie'
import { DisruptionTypeEnum } from '@kvika/api-types'
import { useEffect } from 'react'

import { AudurPrismicClient } from '../prismic/prismicClient'
import {
	AudurContentPage,
	AudurContentPageBody,
	AudurEditorialContentLinkGroup,
	AudurFooter,
	AudurHeader,
	AudurLegal,
	AudurLegalBody,
	AudurLink,
	FaqCategory,
	FaqCategoryBody,
	Maybe,
	MortgageSubPage,
	MortgageSubPageBody,
} from '../prismic/prismicTypes'
import { getNodesFromConnection } from '../prismic/prismicUtils'

import { navigationHeaderHeightSmall, prismicConstants } from './Constants'
import { AudurCookies, MetaTagInfo, ServiceStatusMode, SiteAlert } from './types/Types'

export const stringToInt = (stringToParse: string): number =>
	Number.isInteger(Number.parseInt(stringToParse, 10)) ? Number.parseInt(stringToParse, 10) : 0

/**
 * Cookies
 */
export const isCookieDeclined = (cookie: AudurCookies) => !!(Cookies.get(cookie) && Cookies.get(cookie) === 'false')
export const isCookieAccepted = (cookie: AudurCookies) => !!(!Cookies.get(cookie) || Cookies.get(cookie) === 'true')

const getPrismicMetaTags = (page: FaqCategory | AudurContentPage | AudurLegal | MortgageSubPage) => {
	if (page.body) {
		switch (page.__typename) {
			case 'FaqCategory':
				return page.body.find((slice) => slice.type === 'meta_tags') as FaqCategoryBody
			case 'AudurContentPage':
				return page.body.find((slice) => slice.type === 'meta_tags') as AudurContentPageBody
			case 'AudurLegal':
				return page.body.find((slice) => slice.type === 'meta_tags') as AudurLegalBody
			case 'Mortgage_sub_page':
				return page.body1?.find((slice) => slice.type === 'meta_tags') as MortgageSubPageBody
			default:
				return null
		}
	}
	return null
}

export const getMetaTags = (
	page: Maybe<FaqCategory> | Maybe<AudurContentPage> | Maybe<AudurLegal> | Maybe<MortgageSubPage>
): MetaTagInfo => {
	if (page) {
		const item = getPrismicMetaTags(page)
		if (item) {
			const { primary } = item
			if (primary) {
				return {
					title: primary.metaTitle || '',
					description: primary.metaDescription || '',
					imageUrl: primary.metaImage.url || '',
				}
			}
		}
	}

	return {
		title: '',
		description: '',
		imageUrl: '',
	}
}

export const isValidEmail = (email: string): boolean => {
	const re =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	return re.test(String(email).toLowerCase())
}

export const getHomeLottieImageDimensions = (isMobile: boolean, isTablet: boolean, width: number, height: number) => {
	if (isMobile) {
		return { width: 375, height: 276 }
	}
	if (isTablet) {
		return { width: 350, height: 500 }
	}
	return { width, height }
}

export const getHomeLottieImageOffset = (isMobile: boolean, isTablet: boolean) => {
	if (isMobile) {
		return { x: -20, y: -120 }
	}
	if (isTablet) {
		return { x: -40, y: -140 }
	}
	return { x: -20, y: -100 }
}

/**
 * Scrolls down to the specified HTML element.
 * @param id ID of the element to scroll to.
 */
export const executeScrollToElement = (id: string) => {
	const element = document.getElementById(id)
	const elementPos = element?.getBoundingClientRect().top ?? 0
	window.scrollBy({
		top: elementPos - navigationHeaderHeightSmall,
		behavior: 'smooth',
	})
}

export const getPrismicTitle = (linkGroup?: Maybe<AudurEditorialContentLinkGroup[]>) => {
	const link = linkGroup && (linkGroup[0].editorialContentLink as AudurLink)
	return link?.linkTitle ?? ''
}

export const getPrismicHref = (linkGroup?: Maybe<AudurEditorialContentLinkGroup[]>) => {
	const link = linkGroup && (linkGroup[0].editorialContentLink as AudurLink)
	return link?.linkUrl ?? ''
}

export const getAudurFooter = async () => {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)

	const audurFooters = await audurClient.getFooter({
		variables: { lang: prismicConstants.apiDefaultLocale },
	})
	const extractedNodes: Array<AudurFooter> = getNodesFromConnection(audurFooters)
	return extractedNodes[0]
}

export const getAudurHeader = async () => {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)

	const audurHeaders = await audurClient.getHeader({
		variables: { lang: prismicConstants.apiDefaultLocale },
	})
	const extractedNodes: Array<AudurHeader> = getNodesFromConnection(audurHeaders)
	return extractedNodes[0]
}

const SITE_ALERT_TYPE = 'audur_site_alert'
const SITE_ALERT_TIMEOUT_MS = 3000

// Fixed categories only: error messages can carry request URLs with the access token
type SiteAlertFailure = 'timeout' | 'unauthorized' | 'repository_not_found' | 'api_error' | 'request_failed'

const getSiteAlertFailure = (error: unknown): SiteAlertFailure => {
	if (error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')) {
		return 'timeout'
	}
	if (error instanceof prismic.ForbiddenError) {
		return 'unauthorized'
	}
	if (error instanceof prismic.RepositoryNotFoundError) {
		return 'repository_not_found'
	}
	if (error instanceof prismic.PrismicError) {
		return 'api_error'
	}
	return 'request_failed'
}

// Playwright points this at a local mock. Ignored in production builds, and the access token
// is never sent to it.
const getSiteAlertMockApiUrl = () =>
	process.env.NODE_ENV === 'production' ? undefined : process.env.SITE_ALERT_MOCK_PRISMIC_API_URL

/**
 * Fetches the site-wide alert singleton shared with the online bank.
 *
 * Never throws. Only Prismic content is shown: no document, an empty document or any
 * failure hides the alert, so a Prismic outage can't bring back a finished campaign.
 * Turn it off with enabled=false.
 */
export const getAudurSiteAlert = async (): Promise<SiteAlert> => {
	try {
		const mockApiUrl = getSiteAlertMockApiUrl()
		const client = prismic.createClient(mockApiUrl || prismicConstants.refApiUrl, {
			accessToken: mockApiUrl ? undefined : prismicConstants.apiToken,
			fetchOptions: { signal: AbortSignal.timeout(SITE_ALERT_TIMEOUT_MS) },
		})
		const { results } = await client.getByType(SITE_ALERT_TYPE, {
			pageSize: 1,
			lang: prismicConstants.apiLocale ?? prismicConstants.apiDefaultLocale,
		})
		if (results.length === 0) {
			return null
		}

		const { data } = results[0]
		const now = Date.now()
		const startAt = prismic.asDate(data.start_at)
		const endAt = prismic.asDate(data.end_at)
		const isActive =
			data.enabled === true && (!startAt || startAt.getTime() <= now) && (!endAt || now <= endAt.getTime())
		if (!isActive) {
			return null
		}

		const title = prismic.isFilled.keyText(data.title) ? data.title : null
		const body = prismic.isFilled.richText(data.body) ? data.body : null
		return title || body ? { title, body } : null
	} catch (error) {
		Sentry.addBreadcrumb({
			category: 'prismic',
			level: 'info',
			message: 'getAudurSiteAlert failed; hiding site alert',
			data: { reason: getSiteAlertFailure(error) },
		})
		return null
	}
}

export const getAudurApiClient = (): AudurClient => {
	return new AudurClient({
		apiUrl: process.env.NEXT_PUBLIC_AUDUR_API_URL ?? '',
		storeAuthToken: () => undefined,
	})
}

export const getKvikaApiClient = (setServiceStatusMode?: (serviceStatus: ServiceStatusMode) => void): KvikaClient => {
	return new KvikaClient({
		apiUrl: process.env.NEXT_PUBLIC_KVIKA_API_URL ?? '',
		storeAuthToken: () => undefined,
		appToken: process.env.NEXT_PUBLIC_KVIKA_APP_TOKEN ?? '',
		onResponseHeaders: (headers) => {
			if (setServiceStatusMode) {
				setServiceStatusMode({
					serviceStatus: headers['x-disruption-type'] as DisruptionTypeEnum,
					message: headers['x-disruption-message'],
				})
			}
		},
	})
}

export const formatNumberIS = (value: number): string => {
	if (value === undefined) {
		return '-'
	}

	return value
		.toFixed(0) // Ukloni decimalna mjesta
		.replace(/\B(?=(\d{3})+(?!\d))/g, '.') // Dodaj točku kao separator tisuća
}

export const formatNumberISFloat = (value?: number): string => {
	if (value === undefined) {
		return '-'
	}

	return value
		.toFixed(2) // Zadrži dva decimalna mjesta
		.replace(/\B(?=(\d{3})+(?!\d))/g, '.') // Dodaj točku kao separator tisuća
		.replace(/(\.\d{2})$/, (match) => match.replace('.', ',')) // Zamijeni decimalnu točku zarezom
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
export function useOutsideAlerter(
	ref: React.RefObject<HTMLElement | null>,
	onOutSideClick: () => void,
	ignoredRef: React.RefObject<HTMLElement | null>
) {
	useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		function handleClickOutside(event: MouseEvent) {
			if (
				ref.current &&
				!ref.current.contains(event.target as Node) &&
				ignoredRef.current &&
				!ignoredRef.current.contains(event.target as Node)
			) {
				onOutSideClick()
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [ref, ignoredRef, onOutSideClick])
}
